import React from 'react';

const textMapping = {
  singleCorrect: 'single correct',
  multipleCorrect: 'multiple correct',
  numerical: 'numerical type',
  paragraph: 'paragraph type',
};

const getSectionType = (section) => {
  return textMapping[section?.sectionId?.questions?.[0]?.type];
};

const containsNumericals = (sections) => {
  return sections?.some((s) => {
    return s?.sectionId?.title.includes('Numerical');
  });
};

export const InstructionItem = ({ s }) => {
  const questions = s?.sectionId?.questions || [];
  const questionTypes = { singleCorrect: 0, multipleCorrect: 0, numerical: 0 };

  questions.forEach((question) => {
    if (questionTypes.hasOwnProperty(question.type)) {
      questionTypes[question.type]++;
    }
  });

  const totalQuestions = questions.length;
  const { singleCorrect, multipleCorrect, numerical } = questionTypes;

  const hasMultipleQuestionTypes =
    (singleCorrect > 0 && (multipleCorrect > 0 || numerical > 0)) ||
    (multipleCorrect > 0 && (singleCorrect > 0 || numerical > 0)) ||
    (numerical > 0 && (singleCorrect > 0 || multipleCorrect > 0));

  if (hasMultipleQuestionTypes) {
    return (
      <span>
        <strong>{s.sectionId.title}</strong> contains {totalQuestions} questions.{' '}
        {singleCorrect > 0 && (
          <span>{singleCorrect} are multiple choice questions where only one option is correct. </span>
        )}
        {multipleCorrect > 0 && (
          <span>{multipleCorrect} are multiple choice questions where one or more options are correct. </span>
        )}
        {numerical > 0 && (
          <span>
            {numerical} are numerical questions where you need to provide a numerical answer in the input box provided
            below.
          </span>
        )}
      </span>
    );
  }

  if (s?.sectionId?.questions[0]?.type === 'multipleCorrect') {
    if (s?.sectionId?.markingScheme?.correctAttempt === 4) {
      return (
        <span>
          <strong>{s.sectionId.title}</strong> consisting of <strong>{s.sectionId.questions.length}</strong> multiple
          correct questions. Marks are awarded as per the given conditions:
          <ul>
            <li>Full Marks: +4 only if all the correct options have been chosen.</li>
            <li>
              Partial Marks:
              <ul>
                <li>+3 If all four options are correct but ONLY three correct options are chosen</li>
                <li>+2 If three or more options are correct but only two correct options are chosen</li>
                <li>+1 If two or more options are correct but only one correct option is chosen</li>
              </ul>
            </li>
            <li>Unattempted Questions: 0 mark is awarded</li>
            <li>Negative Marks: {s?.sectionId?.markingScheme?.incorrectAttempt} in all other cases</li>
          </ul>
        </span>
      );
    } else {
      return (
        <span>
          <strong>{s.sectionId.title}</strong> consisting of <strong>{s.sectionId.questions.length}</strong> multiple
          correct questions. Marks are awarded as per the given conditions:
          <ul>
            <li>
              Full Marks: +{s?.sectionId?.markingScheme?.correctAttempt} only if all the correct options have been
              chosen.
            </li>
            <li>
              Partial Marks:
              <ul>
                <li>
                  Awarded only if some of the correct option(s) is/are chosen but no incorrect option(s) is/are chosen
                </li>
                <li>
                  It is calculated as followings: [Number of Correct Option(s) Selected / Total Number of Correct
                  Option(s)] x{s?.sectionId?.markingScheme?.correctAttempt}
                </li>
              </ul>
            </li>
            <li>Unattempted Questions: 0 mark is awarded</li>
            <li>Negative Marks: {s?.sectionId?.markingScheme?.incorrectAttempt} in all other cases</li>
          </ul>
        </span>
      );
    }
  }

  return (
    <span>
      <strong>{s.sectionId.title}</strong> consisting of <strong>{s.sectionId.questions.length}</strong>{' '}
      <strong>{getSectionType(s)}</strong> questions. For each correct response, you will get{' '}
      <strong>+{s.sectionId.markingScheme.correctAttempt}</strong> mark while an incorrect response will get you{' '}
      <strong>{s.sectionId.markingScheme.incorrectAttempt}</strong> mark. <strong>0</strong> will be awarded for no
      response.
    </span>
  );
};

export const GenericInstructions = ({ questionCount, totalTime, totalMarks, sections, subjects }) => {
  return (
    <ul style={{ lineHeight: 2 }} className="mb-0">
      <li>
        <span>
          The total duration of this test is <strong>{totalTime}</strong> minutes.
        </span>
      </li>
      <li>
        <span>
          The test is of <strong>{totalMarks}</strong> marks.
        </span>
      </li>
      <li>
        <span>
          There will be <strong>{questionCount}</strong> questions in the test.
        </span>
      </li>
      <li>
        <span>
          There are <strong>{subjects?.length}</strong> subject(s) in the test:{' '}
          {subjects?.map((s, i) => {
            if (subjects?.length == 1) {
              return (
                <React.Fragment key={i}>
                  <strong>{s.subjectId.title}</strong>
                </React.Fragment>
              );
            } else {
              if (i === subjects?.length - 1) {
                return (
                  <React.Fragment key={i}>
                    and <strong>{s.subjectId.title}</strong>
                  </React.Fragment>
                );
              } else {
                return (
                  <React.Fragment key={i}>
                    {' '}
                    <strong>{s.subjectId.title}</strong>,{' '}
                  </React.Fragment>
                );
              }
            }
          })}
        </span>
      </li>
      <li>
        <span>
          The paper is divided into <strong>{sections?.length}</strong> sections.
        </span>
      </li>
      <li>
        <span>There are following sections:</span>
        <ul>
          {sections?.map((s, i) => {
            return (
              <li key={i}>
                <InstructionItem s={s} />
              </li>
            );
          })}
        </ul>
      </li>
    </ul>
  );
};

export const SpecificInstructions = ({
  ctype,
  chapterName, // This is expected to be an array of objects with title property
  description,
  questionCount,
  totalTime,
  sections,
}) => {
  return (
    <>
      <p>
        {ctype === 'topic' ? (
          <span>
            This is a topic test of the chapter <strong>{chapterName?.[0]?.title}</strong>.
          </span>
        ) : (
          <span>
            This is a chapter test of <strong>{chapterName?.[0]?.title}</strong>.
          </span>
        )}
      </p>
      {ctype === 'topic' && (
        <p>
          <span>
            Questions from following topics will be asked: <strong>{description}</strong>.
          </span>
        </p>
      )}

      <p>
        {containsNumericals(sections) ? (
          <span>
            There are <strong>{questionCount}</strong> questions in this test which contains both multiple choice
            questions and numerical questions.
          </span>
        ) : (
          <span>
            There are <strong>{questionCount}</strong> questions in this test and all are multiple choice questions with
            only one option being correct.
          </span>
        )}
      </p>
      <p>
        <span>
          The duration of the paper is <strong>{totalTime} minutes</strong>.
        </span>
      </p>
      <br />
      <strong>Sections</strong>
      <br />
      <br />
      <ul>
        {sections?.map((s, i) => (
          <li key={i}>
            <span>{s.sectionId.title}</span>
            <br />
            <br />
            {s.sectionId.maximumAttemptLimit !== s.sectionId.questions.length && (
              <p>
                The maximum number of questions that can be attempted in this section -{' '}
                <span>{s.sectionId.maximumAttemptLimit}</span> questions.
              </p>
            )}

            <p>
              <span>
                <strong>{s.sectionId.markingScheme.correctAttempt}</strong> will be awarded for correct attempt.
              </span>
            </p>
            <p>
              <span>
                <strong>{s.sectionId.markingScheme.incorrectAttempt}</strong> will be awarded for incorrect attempt.
              </span>
            </p>
            <p>
              <span>
                <strong>0</strong> will be awarded for no response.
              </span>
            </p>
          </li>
        ))}
      </ul>
    </>
  );
};

export const InstructionsComponent = ({
  ctype,
  chapterName,
  description,
  questionCount,
  totalTime,
  totalMarks,
  sections,
  subjects,
}) => {
  return (
    <>
      {ctype ? (
        <SpecificInstructions
          ctype={ctype}
          chapterName={chapterName}
          description={description}
          questionCount={questionCount}
          totalTime={totalTime}
          sections={sections}
        />
      ) : (
        <GenericInstructions
          questionCount={questionCount}
          totalTime={totalTime}
          totalMarks={totalMarks}
          sections={sections}
          subjects={subjects}
        />
      )}
    </>
  );
};

export const BonusInstructions = ({ bonusSections, questionsCount }) => {
  return (
    <>
      <ul style={{ lineHeight: 2 }}>
        <li>
          This paper also has Bonus section consisting of <strong>{questionsCount}</strong> questions.
        </li>
        <li>
          <span>The Bonus section is divided into the following:</span>
          <ul>
            {bonusSections?.map((s, i) => {
              return (
                <li key={i}>
                  <span>
                    <strong>{s.sectionId.title}</strong> consisting of <strong>{s.sectionId.questions.length}</strong>{' '}
                    <strong>{getSectionType(s)}</strong> questions. For each correct response, you will get{' '}
                    <strong>+{s.sectionId.markingScheme.correctAttempt}</strong> mark while an incorrect response will
                    get you <strong>{s.sectionId.markingScheme.incorrectAttempt}</strong> mark. <strong>0</strong> will
                    be awarded for no response.
                  </span>
                </li>
              );
            })}
          </ul>
        </li>

        <li>
          <span>In order to access the Bonus section, follow the mentioned steps:</span>
          <ul>
            <li>Attempt (Answered or Answered Review) all the normal questions within the time limit</li>
            <li>Click on "Submit" button</li>
            <li>You'll get the option of whether or not you want go for Bonus section.</li>
            <li>Click on "Go to Bonus Questions" button</li>
            <li>
              Important Note: If you select to "Go to Bonus Questions", you cannot go back to the main section (normal
              questions) and you cannot change your responses in the main section.
            </li>
          </ul>
        </li>
      </ul>
    </>
  );
};
